import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { gsap } from "gsap";

const Words = ({ text }) => (
  <>
    {text.split(" ").map((word, i) => (
      <span className="ow-word" key={`${word}-${i}`}>
        <i>{word}</i>
      </span>
    ))}
  </>
);

const TITLE_LEAD = "Why Brands Work With ";
const TITLE_ACCENT = "Us?";

const LINES = [
  "We think in stories, not just posts.",
  "We treat every launch like opening night because it is.",
  "We're small enough to move fast, senior enough to get it right.",
];

const WorkIntro = ({ onDone }) => {
  const rootRef = useRef(null);
  const tlRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      onDone?.();
      return undefined;
    }

    const q = gsap.utils.selector(root);
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        defaults: { ease: "expo.out" },
        onComplete: () => onDone?.(),
      });
      tlRef.current = tl;

      // .ow-stage is `visibility: hidden` by default in CSS — revealed here,
      // in sync with the very first animation frame, so there is no gap
      // where the browser could paint the unstyled text before GSAP touches it.
      tl.set(q(".ow-stage"), { autoAlpha: 1 }, 0);

      // ── Title · words rise out of a mask, blur to sharp ──────────────────
      const titleWords = q(".ow-headline .ow-word i");
      tl.fromTo(
        titleWords,
        { yPercent: 115, filter: "blur(10px)", opacity: 0 },
        {
          yPercent: 0,
          filter: "blur(0px)",
          opacity: 1,
          duration: 0.6,
          stagger: 0.055,
        },
        0
      );

      // Accent draws in right behind the title landing.
      tl.fromTo(
        q(".ow-accent"),
        { scaleX: 0 },
        { scaleX: 1, duration: 0.45, ease: "power3.out" },
        0.32
      );

      // ── Three lines cascade in beneath it, each a beat behind the last ───
      const lineStarts = [0.42, 0.58, 0.74];
      const lineEls = q(".ow-lines .ow-line");
      lineEls.forEach((line, i) => {
        tl.fromTo(
          line.querySelectorAll(".ow-word i"),
          { yPercent: 120, filter: "blur(8px)", opacity: 0 },
          {
            yPercent: 0,
            filter: "blur(0px)",
            opacity: 1,
            duration: 0.55,
            stagger: 0.022,
          },
          lineStarts[i]
        );
      });

      const lastLineEnd = lineStarts[2] + 0.55 + LINES[2].split(" ").length * 0.022;
      const holdUntil = lastLineEnd + 0.65;

      // ── Everything lets go together — no scatter, just a soft release ────
      tl.to(
        q(".ow-headline .ow-word i, .ow-lines .ow-word i"),
        {
          y: -22,
          filter: "blur(12px)",
          opacity: 0,
          duration: 0.55,
          ease: "power2.inOut",
          stagger: { each: 0.008, from: "start" },
        },
        holdUntil
      ).to(
        q(".ow-accent"),
        { scaleX: 0, duration: 0.4, ease: "power2.inOut" },
        holdUntil
      );

      // Only once the type has fully let go does the canvas itself fade —
      // the gallery already sitting behind it is what's revealed.
      tl.to(
        root,
        { autoAlpha: 0, duration: 0.75, ease: "power2.inOut" },
        holdUntil + 0.3
      );
    }, root);

    return () => ctx.revert();
  }, [onDone]);

  const skip = () => {
    tlRef.current?.progress(1);
    tlRef.current?.kill();
    onDone?.();
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" || e.key === "Enter" || e.key === " ") skip();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return createPortal(
    <div className="ow-intro" ref={rootRef} role="presentation">
      <div className="ow-stage">
        <h2 className="ow-headline">
          <Words text={TITLE_LEAD} />
          <span className="section-heading-accent">
            <Words text={TITLE_ACCENT} />
          </span>
        </h2>

        <span className="ow-accent" aria-hidden="true" />

        <div className="ow-lines">
          {LINES.map((line) => (
            <p className="ow-line" key={line}>
              <Words text={line} />
            </p>
          ))}
        </div>
      </div>

      <button type="button" className="ow-skip" onClick={skip}>
        Skip
      </button>
    </div>,
    document.body
  );
};

export default WorkIntro;
